import { useState, useEffect, useCallback } from "react";
import { BarChart3, Car, Package, TrendingUp, Euro, CalendarRange } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  Tooltip, CartesianGrid, BarChart, Cell
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEuro = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);

const MONTH_NAMES = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const fmtMonth = (m) => {
  const [y, mm] = (m || "").split("-");
  return mm ? `${MONTH_NAMES[parseInt(mm, 10) - 1]} ${y?.slice(2)}` : m;
};

const CHART_COLORS = ["#f97316", "#fb923c", "#fdba74", "#ea580c", "#c2410c", "#f59e0b", "#d97706", "#b45309"];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-xs">
      <div className="text-white font-medium mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {p.dataKey.includes("value") ? fmtEuro(p.value) : p.value}
        </div>
      ))}
    </div>
  );
};

export default function AdminQuoteStats({ user }) {
  const [stats, setStats] = useState(null);
  const [officine, setOfficine] = useState([]);
  const [officinaFilter, setOfficinaFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async (officinaId) => {
    setLoading(true);
    try {
      const qs = officinaId && officinaId !== "all" ? `?officina_id=${officinaId}` : "";
      const res = await authFetch(`${API}/admin/stats/confirmed-quotes${qs}`);
      if (res.ok) setStats(await res.json());
      else toast.error("Errore nel caricamento statistiche");
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/admin/users`);
        if (res.ok) {
          const users = await res.json();
          setOfficine(users.filter((u) => (u.user_type || u.role || "").toUpperCase() === "OFFICINA"));
        }
      } catch (e) { /* noop */ }
    };
    load();
    fetchStats("all");
  }, [fetchStats]);

  const handleFilter = (v) => {
    setOfficinaFilter(v);
    fetchStats(v);
  };

  return (
    <div className="space-y-6" data-testid="admin-quote-stats-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-primary" />
            Statistiche Preventivi Confermati
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Analisi per marca veicolo e codice articolo dei preventivi accettati/fatturati dagli automobilisti
          </p>
        </div>
        <div className="w-64">
          <Select value={officinaFilter} onValueChange={handleFilter}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="stats-officina-filter">
              <SelectValue placeholder="Tutte le officine" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 max-h-64">
              <SelectItem value="all">Tutte le officine</SelectItem>
              {officine.map((o) => (
                <SelectItem key={o.user_id} value={o.user_id}>{o.company_name || o.username}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-zinc-500 py-16">Caricamento...</div>
      ) : stats && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white" data-testid="stats-confirmed-count">{stats.summary.confirmed_quotes}</div>
                <div className="text-xs text-zinc-500 uppercase">Preventivi Confermati</div>
              </div>
            </div>
            <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Euro className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{fmtEuro(stats.summary.total_value)}</div>
                <div className="text-xs text-zinc-500 uppercase">Valore Totale</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly trend chart */}
            <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6" data-testid="stats-monthly-chart">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <CalendarRange className="w-5 h-5 text-primary" />
                Andamento Mensile
              </h2>
              {(!stats.monthly_trend || stats.monthly_trend.length === 0) ? (
                <div className="text-center text-zinc-500 py-8">Nessun dato</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={stats.monthly_trend.map((m) => ({ ...m, label: fmtMonth(m.month) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
                    <YAxis yAxisId="left" stroke="#71717a" fontSize={11} allowDecimals={false} />
                    <YAxis yAxisId="right" orientation="right" stroke="#71717a" fontSize={11}
                      tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar yAxisId="left" dataKey="quotes" name="Preventivi" fill="#f97316" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="total_value" name="Valore" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Brand chart */}
            <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6" data-testid="stats-brand-chart">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-primary" />
                Top Marche Veicolo
              </h2>
              {stats.by_vehicle_brand.length === 0 ? (
                <div className="text-center text-zinc-500 py-8">Nessun dato</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stats.by_vehicle_brand.slice(0, 8)} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis type="number" stroke="#71717a" fontSize={11} allowDecimals={false} />
                    <YAxis type="category" dataKey="brand" stroke="#a1a1aa" fontSize={11} width={90} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="quotes" name="Preventivi" radius={[0, 4, 4, 0]}>
                      {stats.by_vehicle_brand.slice(0, 8).map((b, i) => (
                        <Cell key={b.brand} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By vehicle brand */}
            <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6" data-testid="stats-by-brand-table">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-primary" />
                Per Marca Veicolo
              </h2>
              {stats.by_vehicle_brand.length === 0 ? (
                <div className="text-center text-zinc-500 py-8">Nessun dato</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-zinc-500 text-left border-b border-zinc-800">
                      <th className="py-2">Marca</th>
                      <th className="py-2 text-right">Preventivi</th>
                      <th className="py-2 text-right">Valore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_vehicle_brand.map((b) => (
                      <tr key={b.brand} className="border-b border-zinc-800/50">
                        <td className="py-2.5 text-white font-medium">{b.brand}</td>
                        <td className="py-2.5 text-right text-zinc-300">{b.quotes}</td>
                        <td className="py-2.5 text-right text-zinc-300">{fmtEuro(b.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* By article */}
            <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6" data-testid="stats-by-article-table">
              <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Per Articolo (Marca + Codice)
              </h2>
              {stats.by_article.length === 0 ? (
                <div className="text-center text-zinc-500 py-8">Nessun dato</div>
              ) : (
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-zinc-900">
                      <tr className="text-zinc-500 text-left border-b border-zinc-800">
                        <th className="py-2 pr-3">Codice</th>
                        <th className="py-2 pr-3">Marca</th>
                        <th className="py-2 pr-3">Descrizione</th>
                        <th className="py-2 text-right pr-3">Qtà</th>
                        <th className="py-2 text-right">Valore</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.by_article.map((a) => (
                        <tr key={`${a.item_code}-${a.brand}-${a.description}`} className="border-b border-zinc-800/50">
                          <td className="py-2.5 pr-3 font-mono text-primary text-xs">{a.item_code}</td>
                          <td className="py-2.5 pr-3 text-zinc-300">{a.brand}</td>
                          <td className="py-2.5 pr-3 text-white text-xs">{a.description}</td>
                          <td className="py-2.5 pr-3 text-right text-zinc-300">{a.total_qty}</td>
                          <td className="py-2.5 text-right text-zinc-300">{fmtEuro(a.total_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
